import { useCallback, useMemo, useState } from 'react';
import { ApiError, scanApp, startScan, updateAdUnits, type AppSummary } from './api';
import { pooledForEach } from './pool';
import { AppRow, type AppRowState } from './AppRow';

/** How many apps we scan in parallel. Bounded so we don't hammer ironSource. */
const SCAN_CONCURRENCY = 8;

/**
 * Accident guard, not a security control: rotating twice in one day would skip a
 * version. This lives in localStorage and is trivially cleared — it exists to
 * stop a double-click, not an attacker.
 */
const UPDATE_LIMIT = 2;
const UPDATE_KEY = 'updateApiUsage';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function readUsage(): number {
  try {
    const raw = localStorage.getItem(UPDATE_KEY);
    if (!raw) return 0;
    const parsed = JSON.parse(raw) as { date?: string; count?: number };
    return parsed.date === today() ? (parsed.count ?? 0) : 0;
  } catch {
    return 0;
  }
}

function bumpUsage(): void {
  try {
    localStorage.setItem(UPDATE_KEY, JSON.stringify({ date: today(), count: readUsage() + 1 }));
  } catch {
    // Private browsing or a full quota. The guard degrades, the update still works.
  }
}

const IDLE_ROW: AppRowState = { status: 'idle', expanded: false, adUnits: [] };

/**
 * Shared input styling.
 *
 * border-gray-300 is explicit on purpose: Tailwind v3 gave a bare `border` a
 * default gray border colour, but v4 dropped that default and falls back to
 * currentColor — which rendered these inputs with heavy dark borders.
 */
const INPUT_CLASS =
  'w-full rounded border border-gray-300 px-3 py-2 text-gray-700 shadow-sm ' +
  'focus:border-blue-500 focus:ring-2 focus:ring-blue-200 focus:outline-none';

type Notice = { tone: 'info' | 'success' | 'warn' | 'error'; text: string } | null;

const NOTICE_STYLES: Record<NonNullable<Notice>['tone'], string> = {
  info: 'bg-blue-50 border-blue-300 text-blue-800',
  success: 'bg-green-50 border-green-300 text-green-800',
  warn: 'bg-yellow-50 border-yellow-300 text-yellow-900',
  error: 'bg-red-50 border-red-300 text-red-800',
};

export default function App() {
  const [secretKey, setSecretKey] = useState('');
  const [refreshToken, setRefreshToken] = useState('');
  const [authToken, setAuthToken] = useState<string | null>(null);
  const [apps, setApps] = useState<AppSummary[]>([]);
  const [rows, setRows] = useState<Record<string, AppRowState>>({});
  const [search, setSearch] = useState('');
  const [busy, setBusy] = useState(false);
  const [scanningAll, setScanningAll] = useState(false);
  const [scanProgress, setScanProgress] = useState(0);
  const [updating, setUpdating] = useState(false);
  const [updateDone, setUpdateDone] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);
  const [usage, setUsage] = useState(readUsage);

  const patchRow = useCallback((appKey: string, patch: Partial<AppRowState>) => {
    setRows((current) => ({ ...current, [appKey]: { ...(current[appKey] ?? IDLE_ROW), ...patch } }));
  }, []);

  const loadApp = useCallback(
    async (app: AppSummary, token: string) => {
      patchRow(app.app_key, { status: 'loading' });
      try {
        const result = await scanApp(token, app);
        patchRow(app.app_key, { status: 'loaded', adUnits: result.ad_units, error: undefined });
      } catch (error) {
        patchRow(app.app_key, {
          status: 'error',
          adUnits: [],
          error: error instanceof Error ? error.message : 'App scan failed',
        });
      }
    },
    [patchRow]
  );

  async function handleLogin(event: React.FormEvent) {
    event.preventDefault();
    setBusy(true);
    setNotice({ tone: 'info', text: 'Authenticating with ironSource…' });

    try {
      const result = await startScan(secretKey.trim(), refreshToken.trim());
      setAuthToken(result.auth_token);
      setApps(result.apps);
      setRows(Object.fromEntries(result.apps.map((app) => [app.app_key, IDLE_ROW])));
      setNotice(
        result.apps.length
          ? null
          : { tone: 'warn', text: 'Login succeeded, but no active apps were returned.' }
      );
      // Credentials are not needed again; the bearer token carries the session.
      setSecretKey('');
      setRefreshToken('');
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Could not start the scan.';
      setNotice({ tone: 'error', text: message });
    } finally {
      setBusy(false);
    }
  }

  function toggleRow(app: AppSummary) {
    const row = rows[app.app_key] ?? IDLE_ROW;
    const willExpand = !row.expanded;
    patchRow(app.app_key, { expanded: willExpand });

    if (willExpand && row.status === 'idle' && authToken && !scanningAll) {
      void loadApp(app, authToken);
    }
  }

  async function handleScanAll() {
    if (!authToken || scanningAll) return;

    setScanningAll(true);
    setScanProgress(0);
    setNotice({ tone: 'info', text: `Scanning ${apps.length} apps…` });

    let done = 0;
    await pooledForEach(apps, SCAN_CONCURRENCY, async (app) => {
      await loadApp(app, authToken);
      done += 1;
      setScanProgress(done);
    });

    setScanningAll(false);
    setNotice(null);
  }

  const readyApps = useMemo(
    () =>
      apps
        .map((app) => ({ app, row: rows[app.app_key] }))
        .filter((entry) => entry.row?.status === 'loaded' && entry.row.adUnits.length > 0),
    [apps, rows]
  );

  const scannedCount = useMemo(
    () => Object.values(rows).filter((row) => row.status === 'loaded' || row.status === 'error').length,
    [rows]
  );

  async function handleUpdate() {
    if (!authToken || !readyApps.length || updating) return;

    if (usage >= UPDATE_LIMIT) {
      setNotice({
        tone: 'warn',
        text: `Daily limit of ${UPDATE_LIMIT} updates reached. Clear site data to override if you really mean to.`,
      });
      return;
    }

    const total = readyApps.reduce((sum, entry) => sum + entry.row!.adUnits.length, 0);
    if (!window.confirm(`Write ${total} new ad unit ID(s) across ${readyApps.length} app(s) to ironSource?`)) {
      return;
    }

    setUpdating(true);
    setNotice({ tone: 'info', text: 'Applying ad unit updates…' });

    try {
      const payload = readyApps.map((entry) => ({
        app_key: entry.app.app_key,
        app_name: entry.app.app_name,
        ad_units: entry.row!.adUnits,
      }));
      const { results, message, partial } = await updateAdUnits(authToken, payload);

      setRows((current) => {
        const next = { ...current };
        for (const result of results) {
          const row = next[result.app_key];
          if (!row) continue;
          next[result.app_key] = {
            ...row,
            updateSucceeded: result.success,
            instanceStatus: Object.fromEntries(
              result.status.map((entry) => [String(entry.instanceId), entry.statusCode])
            ),
          };
        }
        return next;
      });

      bumpUsage();
      setUsage(readUsage());
      setUpdateDone(true);
      setNotice({
        tone: partial ? 'warn' : 'success',
        text: message || (partial ? 'Some updates failed.' : 'Ad units updated.'),
      });
    } catch (error) {
      setNotice({
        tone: 'error',
        text: error instanceof Error ? error.message : 'Update failed.',
      });
    } finally {
      setUpdating(false);
    }
  }

  const visibleApps = useMemo(() => {
    const query = search.trim().toLowerCase();
    return query ? apps.filter((app) => app.app_name.toLowerCase().includes(query)) : apps;
  }, [apps, search]);

  // --- credential screen -------------------------------------------------
  if (!authToken) {
    return (
      <main className="min-h-screen bg-gray-100 px-4 py-10">
        <div className="mx-auto w-full max-w-md rounded-lg bg-white p-8 shadow-lg">
          <h1 className="mb-6 text-center text-2xl font-bold text-gray-800">ironSource Refresh</h1>

          <form onSubmit={handleLogin}>
            <label className="mb-2 block text-sm font-bold text-gray-700" htmlFor="secretKey">
              Secret Key
            </label>
            <input
              id="secretKey"
              type="password"
              autoComplete="off"
              required
              value={secretKey}
              onChange={(event) => setSecretKey(event.target.value)}
              placeholder="Enter ironSource Secret Key"
              className={`mb-4 ${INPUT_CLASS}`}
            />

            <label className="mb-2 block text-sm font-bold text-gray-700" htmlFor="refreshToken">
              Refresh Token
            </label>
            <input
              id="refreshToken"
              type="password"
              autoComplete="off"
              required
              value={refreshToken}
              onChange={(event) => setRefreshToken(event.target.value)}
              placeholder="Enter ironSource Refresh Token"
              className={`mb-6 ${INPUT_CLASS}`}
            />

            <button
              type="submit"
              disabled={busy}
              className="w-full rounded bg-blue-500 py-2 font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {busy ? 'Authenticating…' : 'Submit'}
            </button>
          </form>

          {notice && (
            <div className={`mt-6 rounded border p-4 text-sm ${NOTICE_STYLES[notice.tone]}`} role="status">
              {notice.text}
            </div>
          )}
        </div>
      </main>
    );
  }

  // --- app list ----------------------------------------------------------
  const limitReached = usage >= UPDATE_LIMIT;

  return (
    <main className="min-h-screen bg-gray-100 px-4 py-6 md:py-10">
      <div className="mx-auto w-full max-w-6xl rounded-lg bg-white p-6 shadow-lg md:p-8">
        <h1 className="mb-6 text-center text-2xl font-bold text-gray-800">Apps</h1>

        <div className="flex flex-col gap-3 rounded-lg border border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 md:flex-row md:items-center md:justify-between">
          <div>
            <p className="font-semibold text-gray-800">{apps.length} apps found</p>
            <p className="mt-1 text-gray-600">
              {scannedCount} scanned, {readyApps.length} with ad units
            </p>
          </div>
          <div className="flex w-full flex-col gap-2 sm:flex-row md:w-auto">
            <button
              type="button"
              onClick={handleScanAll}
              disabled={scanningAll}
              className="rounded bg-blue-600 px-4 py-2 text-sm font-bold text-white hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {scanningAll ? `Scanning ${scanProgress}/${apps.length}` : 'Scan All Apps'}
            </button>
            <input
              type="search"
              value={search}
              onChange={(event) => setSearch(event.target.value)}
              placeholder="Search apps"
              className="w-full rounded border border-gray-300 px-3 py-2 text-sm md:w-72"
            />
          </div>
        </div>

        {notice && (
          <div className={`mt-4 rounded border p-4 text-sm ${NOTICE_STYLES[notice.tone]}`} role="status">
            {notice.text}
          </div>
        )}

        {scanningAll && (
          <p className="mt-3 text-xs text-gray-500">
            Keep this tab open — closing it stops the scan.
          </p>
        )}

        <div className="mt-4 max-h-[68vh] space-y-3 overflow-y-auto pr-2">
          {visibleApps.map((app) => (
            <AppRow
              key={app.app_key}
              appName={app.app_name}
              state={rows[app.app_key] ?? IDLE_ROW}
              onToggle={() => toggleRow(app)}
            />
          ))}
          {!visibleApps.length && (
            <p className="rounded-lg bg-gray-50 p-4 text-sm text-gray-600">No apps match that search.</p>
          )}
        </div>

        <div className="sticky bottom-0 mt-4 flex items-center justify-end gap-4 border-t border-gray-200 bg-white py-4">
          {limitReached && (
            <span className="text-xs text-gray-500">Daily update limit reached ({usage}/{UPDATE_LIMIT})</span>
          )}
          <button
            type="button"
            onClick={handleUpdate}
            disabled={updating || updateDone || limitReached || !readyApps.length}
            className="rounded-lg bg-indigo-600 px-6 py-3 font-bold text-white hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {updating
              ? 'Updating…'
              : updateDone
                ? 'Updated'
                : `Update ${readyApps.length} Loaded App${readyApps.length === 1 ? '' : 's'}`}
          </button>
        </div>
      </div>
    </main>
  );
}
